import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Coins, RefreshCw, Save } from "lucide-react";
import { canAccessModule } from "@/utils/permissions";
import "./cabina.css";

export default function AdminMonedasConfig() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({}); // {code: {is_active, incremento}}
  const [saving, setSaving] = useState(null);
  const canView = canAccessModule(user, "admin_monedas_config");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/monedas/config`);
      setRows(r.data.currencies || []);
      setDirty({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const change = (code, field, val) => {
    setDirty((d) => ({ ...d, [code]: { ...(d[code] || {}), [field]: val } }));
  };

  const guardar = async (code) => {
    const body = dirty[code];
    if (!body) return;
    setSaving(code);
    try {
      await axios.patch(`${API}/monedas/config/${code}`, body);
      toast.success(`${code} actualizada`);
      // Notificar a la barra superior y a la calculadora para refrescar TC
      window.dispatchEvent(new Event("tc-updated"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(null);
  };

  const refreshBanxico = async () => {
    try {
      await axios.post(`${API}/monedas/tc/refresh-banxico`);
      toast.success("Banxico actualizado (USD/EUR)");
      window.dispatchEvent(new Event("tc-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!canView) {
    return (
      <div className="cv" style={{ padding: 22 }}>
        <div className="rulebox" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
          <b>Sin acceso.</b> No tienes permiso para configurar monedas.
        </div>
      </div>
    );
  }

  return (
    <div className="cv" style={{ padding: 22 }} data-testid="admin-monedas-config">
      <div className="head">
        <div>
          <h2><Coins size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Configuración de Monedas</h2>
          <p>Activa las divisas que operarás en el sistema y define el incremento sobre el TC oficial de BANXICO. El incremento se aplica al día actual y hacia adelante; los registros históricos conservan el incremento vigente al momento de su captura.</p>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn ghost sm" onClick={refreshBanxico} data-testid="btn-refresh-banxico">
            <RefreshCw size={12} /> Actualizar Banxico
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="tw">
          {loading ? (
            <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Cargando…</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th style={{ textAlign: "center" }}>Símbolo</th>
                  <th style={{ textAlign: "center" }}>Fuente</th>
                  <th style={{ textAlign: "center", width: 90 }}>Activa</th>
                  <th className="r" style={{ width: 160 }}>Incremento (MXN)</th>
                  <th className="r" style={{ width: 110 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const changed = !!dirty[c.code];
                  const val = dirty[c.code] || {};
                  const isMxn = c.code === "MXN";
                  return (
                    <tr key={c.code} data-testid={`row-cur-${c.code}`}>
                      <td className="mono" style={{ color: "var(--cv-muted)" }}>{c.orden}</td>
                      <td><b style={{ color: "var(--cv-blue)" }}>{c.code}</b></td>
                      <td>{c.name}</td>
                      <td style={{ textAlign: "center" }}>{c.symbol}</td>
                      <td style={{ textAlign: "center", fontSize: 11.5 }}>
                        {c.banxico_series ? <span className="chip c-val">Banxico</span> : <span className="chip c-pend">Manual</span>}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" disabled={isMxn}
                          checked={val.is_active !== undefined ? val.is_active : c.is_active}
                          onChange={(e) => change(c.code, "is_active", e.target.checked)}
                          data-testid={`chk-active-${c.code}`} />
                      </td>
                      <td className="r">
                        <input type="number" step="0.0001" disabled={isMxn}
                          value={val.incremento !== undefined ? val.incremento : (c.incremento ?? 0)}
                          onChange={(e) => change(c.code, "incremento", Number(e.target.value || 0))}
                          style={{ width: 130, textAlign: "right" }}
                          data-testid={`inp-inc-${c.code}`} />
                      </td>
                      <td className="r">
                        <button className="btn sm" disabled={!changed || saving === c.code}
                          onClick={() => guardar(c.code)} data-testid={`btn-save-${c.code}`}>
                          <Save size={12} /> {saving === c.code ? "…" : "Guardar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p style={{ marginTop: 12, color: "var(--cv-muted)", fontSize: 12 }}>
        Sólo USD y EUR se descargan automáticamente desde BANXICO. Para las demás monedas, captura el TC diariamente en <b>Tipos de Cambio</b>.
      </p>
    </div>
  );
}
